import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useId, useRef, useState } from 'react';

import { normalizeHandle } from '../../shared/auth/student-login';
import { Button } from '../../shared/ui/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader } from '../../shared/ui/Dialog';
import { StatusBanner } from '../../shared/ui/StatusBanner';
import { Table, Tbody, Td, Th, Thead, Tr } from '../../shared/ui/Table';

import {
  INSTITUTE_QUERY_KEYS,
  inviteStudentsBulk,
  provisionStudentsBulk,
  type InviteStudentBulkResponse,
  type InviteStudentRowInput,
  type ProvisionStudentBulkResponse,
  type ProvisionStudentRowInput,
} from './api';
import { downloadCredentialsCsv } from './credential-export';
import s from './ImportStudentsDialog.module.css';
import { credentialCsvRowSchema, csvRowSchema } from './schemas';

interface Props {
  instituteId: string;
  open: boolean;
  onClose: () => void;
}

type Mode = 'credential' | 'email';

type ParsedData =
  | { kind: 'credential'; row: ProvisionStudentRowInput }
  | { kind: 'email'; row: InviteStudentRowInput };

interface ParsedRow {
  index: number;
  raw: Record<string, string>;
  data: ParsedData | null;
  errors: string[];
}

type BulkResult =
  | { kind: 'credential'; response: ProvisionStudentBulkResponse }
  | { kind: 'email'; response: InviteStudentBulkResponse };

const MAX_ROWS = 500;

const TEMPLATE_HEADERS: Record<Mode, string[]> = {
  credential: ['username', 'name', 'phone', 'roll_no', 'exam_type', 'target_band'],
  email: ['email', 'name', 'phone', 'roll_no', 'exam_type', 'target_band'],
};

function normalizeRow(raw: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    out[key.trim().toLowerCase()] = typeof value === 'string' ? value.trim() : '';
  }
  return out;
}

function validateRow(mode: Mode, index: number, raw: Record<string, string>): ParsedRow {
  if (mode === 'credential') {
    const parsed = credentialCsvRowSchema.safeParse(raw);
    if (!parsed.success) {
      return {
        index,
        raw,
        data: null,
        errors: parsed.error.issues.map((i) => `${i.path.join('.') || 'row'}: ${i.message}`),
      };
    }
    const v = parsed.data;
    return {
      index,
      raw,
      errors: [],
      data: {
        kind: 'credential',
        row: {
          username: v.username,
          name: v.name,
          phone: v.phone === '' ? null : v.phone,
          roll_no: v.roll_no === '' ? null : v.roll_no,
          exam_type: v.exam_type as 'academic' | 'general',
          target_band: v.target_band === '' ? null : parseFloat(v.target_band),
        },
      },
    };
  }
  const parsed = csvRowSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      index,
      raw,
      data: null,
      errors: parsed.error.issues.map((i) => `${i.path.join('.') || 'row'}: ${i.message}`),
    };
  }
  const v = parsed.data;
  return {
    index,
    raw,
    errors: [],
    data: {
      kind: 'email',
      row: {
        email: v.email,
        name: v.name,
        phone: v.phone,
        roll_no: v.roll_no === '' ? null : v.roll_no,
        exam_type: v.exam_type as 'academic' | 'general',
        target_band: v.target_band === '' ? null : parseFloat(v.target_band),
      },
    },
  };
}

export function ImportStudentsDialog({ instituteId, open, onClose }: Props): JSX.Element {
  const titleId = useId();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<Mode>('credential');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>('');

  const reset = useCallback(() => {
    setRows([]);
    setParseError(null);
    setResult(null);
    setServerError(null);
    setFilename('');
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const switchMode = useCallback(
    (next: Mode) => {
      if (next === mode) return;
      reset();
      setMode(next);
    },
    [mode, reset],
  );

  const handleFile = useCallback(
    async (file: File) => {
      setParseError(null);
      setResult(null);
      setServerError(null);
      setParsing(true);
      setFilename(file.name);
      try {
        // Lazy-load PapaParse so the ~14KB doesn't bloat the institute route
        // chunk for users who never import.
        const Papa = (await import('papaparse')).default;
        const text = await file.text();
        const parsed = Papa.parse<Record<string, unknown>>(text, {
          header: true,
          skipEmptyLines: 'greedy',
          transformHeader: (h: string) => h.trim().toLowerCase(),
        });
        if (parsed.errors.length > 0) {
          setParseError(`CSV parse error: ${parsed.errors[0]?.message ?? 'unknown'}`);
          setRows([]);
          return;
        }
        const data = parsed.data;
        if (data.length === 0) {
          setParseError('No rows found in CSV.');
          setRows([]);
          return;
        }
        if (data.length > MAX_ROWS) {
          setParseError(`Too many rows: ${data.length}. Max ${MAX_ROWS} per import.`);
          setRows([]);
          return;
        }
        const validated = data.map((r, i) => validateRow(mode, i + 2, normalizeRow(r)));
        setRows(validated);
      } catch (e) {
        setParseError(e instanceof Error ? e.message : 'Failed to read file');
        setRows([]);
      } finally {
        setParsing(false);
      }
    },
    [mode],
  );

  const mutation = useMutation({
    mutationFn: async (): Promise<BulkResult> => {
      const valid = rows.map((r) => r.data).filter((d): d is ParsedData => d !== null);
      if (mode === 'credential') {
        const credRows = valid
          .filter((d): d is Extract<ParsedData, { kind: 'credential' }> => d.kind === 'credential')
          .map((d) => d.row);
        return { kind: 'credential', response: await provisionStudentsBulk(instituteId, credRows) };
      }
      const inviteRows = valid
        .filter((d): d is Extract<ParsedData, { kind: 'email' }> => d.kind === 'email')
        .map((d) => d.row);
      return { kind: 'email', response: await inviteStudentsBulk(instituteId, inviteRows) };
    },
    onSuccess: (res) => {
      setResult(res);
      if (res.response.succeeded > 0) {
        void queryClient.invalidateQueries({
          queryKey: INSTITUTE_QUERY_KEYS.students(instituteId),
        });
        void queryClient.invalidateQueries({
          queryKey: INSTITUTE_QUERY_KEYS.stats.studentsCount(instituteId),
        });
      }
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const validCount = rows.filter((r) => r.data !== null).length;
  const invalidCount = rows.length - validCount;
  const canSend = validCount > 0 && !mutation.isPending && result === null;

  // Per-row server-result lookup: credential keyed by username, email by email.
  const serverStatus = new Map<
    string,
    { ok: boolean; error: string | undefined; created: string }
  >();
  if (result !== null) {
    if (result.kind === 'credential') {
      for (const r of result.response.results) {
        serverStatus.set(r.username, { ok: r.ok, error: r.error, created: 'Login created' });
      }
    } else {
      for (const r of result.response.results) {
        serverStatus.set(r.email, {
          ok: r.ok,
          error: r.error,
          created: r.invite_sent ? 'Invited' : 'Linked',
        });
      }
    }
  }

  function rowKey(row: ParsedRow): string | null {
    if (row.data === null) return null;
    return row.data.kind === 'credential'
      ? normalizeHandle(row.data.row.username)
      : row.data.row.email;
  }

  function primaryValue(row: ParsedRow): string {
    return mode === 'credential' ? (row.raw['username'] ?? '') : (row.raw['email'] ?? '');
  }

  function handleDownload(): void {
    if (result === null || result.kind !== 'credential') return;
    const nameByHandle = new Map<string, string>();
    for (const r of rows) {
      if (r.data?.kind === 'credential') {
        nameByHandle.set(normalizeHandle(r.data.row.username), r.data.row.name);
      }
    }
    const exportRows = result.response.results
      .filter((r) => r.ok && r.password !== null)
      .map((r) => ({
        name: nameByHandle.get(r.username) ?? '',
        username: r.username,
        password: r.password as string,
        loginUrl: window.location.origin + r.login_path,
      }));
    downloadCredentialsCsv(exportRows);
  }

  const primaryHeader = mode === 'credential' ? 'Username' : 'Email';
  const succeeded = result?.response.succeeded ?? 0;
  const processed = result?.response.processed ?? 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      labelledById={titleId}
      size="lg"
      mobilePresentation="sheet"
    >
      <DialogHeader
        titleId={titleId}
        title="Import students from CSV"
        subtitle={`Up to ${MAX_ROWS} rows. Required: ${
          mode === 'credential' ? 'username, name' : 'email, name, phone'
        }. Optional: ${mode === 'credential' ? 'phone, ' : ''}roll_no, exam_type, target_band.`}
        onClose={handleClose}
      />
      <DialogBody>
        <div className={s.modeToggle} role="group" aria-label="How the students sign in">
          <button
            type="button"
            className={`${s.modeBtn ?? ''} ${mode === 'credential' ? (s.modeBtnActive ?? '') : ''}`.trim()}
            aria-pressed={mode === 'credential'}
            onClick={() => switchMode('credential')}
          >
            Create logins
          </button>
          <button
            type="button"
            className={`${s.modeBtn ?? ''} ${mode === 'email' ? (s.modeBtnActive ?? '') : ''}`.trim()}
            aria-pressed={mode === 'email'}
            onClick={() => switchMode('email')}
          >
            Email invites
          </button>
        </div>

        <div className={s.dropZone}>
          <p className={s.dropHint}>
            Drop a <code>.csv</code> file here, or
          </p>
          <label className={s.fileBtn}>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className={s.fileInput}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
            <span>Choose CSV…</span>
          </label>
          {filename !== '' ? (
            <p className={s.fileName} aria-live="polite">
              Selected: <code>{filename}</code>
            </p>
          ) : null}
          <details className={s.template}>
            <summary>Template headers</summary>
            {/* eslint-disable-next-line security/detect-object-injection -- `mode` is a literal union */}
            <code>{TEMPLATE_HEADERS[mode].join(',')}</code>
          </details>
        </div>

        {parsing ? <p className={s.status}>Parsing…</p> : null}

        {parseError !== null ? (
          <div className={s.banner}>
            <StatusBanner tone="error">{parseError}</StatusBanner>
          </div>
        ) : null}

        {rows.length > 0 ? (
          <div className={s.preview} role="region" aria-label="CSV preview">
            <div className={s.previewHead}>
              <span className={s.previewCount}>
                {rows.length} row{rows.length === 1 ? '' : 's'} parsed —{' '}
                <strong className={s.ok}>{validCount} valid</strong>
                {invalidCount > 0 ? (
                  <>
                    {' · '}
                    <strong className={s.err}>{invalidCount} with errors</strong>
                  </>
                ) : null}
              </span>
            </div>
            <Table
              caption="Rows to import"
              collapse="scroll"
              variant="plain"
              stickyHeader
              minWidth="24rem"
              className={s.previewTable ?? ''}
            >
              <Thead>
                <Tr>
                  <Th className={`${s.colNum ?? ''} ${s.colNumHead ?? ''}`.trim()}>Line</Th>
                  <Th>{primaryHeader}</Th>
                  <Th>Name</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => {
                  const key = rowKey(row);
                  const serverRow = key !== null ? serverStatus.get(key) : undefined;
                  const hasClientErr = row.errors.length > 0;
                  const hasServerErr = serverRow !== undefined && !serverRow.ok;
                  return (
                    <Tr
                      key={row.index}
                      className={hasClientErr || hasServerErr ? s.rowErr : s.rowOk}
                    >
                      <Td className={s.colNum}>{row.index}</Td>
                      <Td>{primaryValue(row)}</Td>
                      <Td>{row.raw['name'] ?? ''}</Td>
                      <Td>
                        {hasClientErr ? (
                          <span className={s.badgeErr}>{row.errors.join('; ')}</span>
                        ) : hasServerErr ? (
                          <span className={s.badgeErr}>{serverRow.error ?? 'Failed'}</span>
                        ) : serverRow?.ok === true ? (
                          <span className={s.badgeOk}>{serverRow.created}</span>
                        ) : (
                          <span className={s.badgeReady}>Ready</span>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </div>
        ) : null}

        {serverError !== null ? (
          <div className={s.banner}>
            <StatusBanner tone="error">{serverError}</StatusBanner>
          </div>
        ) : null}

        {result !== null ? (
          <div className={s.banner}>
            <StatusBanner tone={succeeded === processed ? 'success' : 'warning'}>
              {succeeded} of {processed} {mode === 'credential' ? 'logins created' : 'invites sent'}
              .
              {result.kind === 'credential' && succeeded > 0
                ? ' Download the credential sheet now — the passwords are shown only once.'
                : succeeded < processed
                  ? ' Review the rows marked with errors above.'
                  : ''}
            </StatusBanner>
          </div>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={handleClose}>
          {result !== null ? 'Done' : 'Cancel'}
        </Button>
        {result !== null && result.kind === 'credential' && succeeded > 0 ? (
          <Button type="button" onClick={handleDownload}>
            Download credential sheet
          </Button>
        ) : null}
        {result === null ? (
          <Button
            type="button"
            loading={mutation.isPending}
            disabled={!canSend}
            onClick={() => {
              setServerError(null);
              mutation.mutate();
            }}
          >
            {mode === 'credential'
              ? `Create ${validCount} login${validCount === 1 ? '' : 's'}`
              : `Send ${validCount} invite${validCount === 1 ? '' : 's'}`}
          </Button>
        ) : null}
      </DialogFooter>
    </Dialog>
  );
}
